import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { getApiBase } from "../api";

// ============================================================================
// CARRIER LOAD DETAIL — Mobile-Optimized
// Full-width pipeline, large action buttons, tap-to-call, safe areas
// Bottom nav hidden on this page (via CarrierLayout regex)
// ============================================================================

// Handle address fields that may be objects {city, line1, state, postal} or strings
function fmtAddr(addr, city, state) {
  if (addr && typeof addr === "object") {
    const parts = [addr.line1, addr.city, addr.state, addr.postal].filter(Boolean);
    return parts.join(", ") || `${city || "?"}, ${state || ""}`;
  }
  if (addr && typeof addr === "string") return addr;
  return `${city || "?"}, ${state || ""}`;
}

const STATUS_PIPELINE = [
  { key: "BOOKED",      label: "Booked",      icon: "📝" },
  { key: "DISPATCHED",  label: "Dispatch",     icon: "📤" },
  { key: "AT_PICKUP",   label: "Pickup",       icon: "📦" },
  { key: "PICKED_UP",   label: "Picked",       icon: "✅" },
  { key: "IN_TRANSIT",  label: "Transit",      icon: "🚛" },
  { key: "AT_DELIVERY", label: "Delivery",     icon: "🏭" },
  { key: "DELIVERED",   label: "Delivered",    icon: "✅" },
];

function StatusPipeline({ current }) {
  const idx = STATUS_PIPELINE.findIndex(s => s.key === current);
  return (
    <div style={{ display: "flex", gap: 2, padding: "0 2px" }}>
      {STATUS_PIPELINE.map((step, i) => {
        const done = i <= idx;
        const active = i === idx;
        return (
          <div key={step.key} style={{ flex: 1, textAlign: "center" }}>
            <div style={{
              height: 4, borderRadius: 2, marginBottom: 4,
              background: done ? "#22c55e" : "rgba(255,255,255,0.08)",
              transition: "background 0.3s",
            }} />
            <div style={{
              fontSize: 9, fontWeight: active ? 800 : 500, lineHeight: 1.2,
              color: active ? "#22c55e" : done ? "#64748b" : "#334155",
            }}>
              {active && <span style={{ fontSize: 11 }}>{step.icon} </span>}
              {step.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActionBtn({ icon, label, color, onClick, disabled, large }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", gap: 4,
      padding: large ? "18px 8px" : "14px 8px",
      borderRadius: 14, border: "none", cursor: disabled ? "default" : "pointer",
      background: disabled ? "rgba(255,255,255,0.03)" : `${color}12`,
      color: disabled ? "#334155" : color,
      opacity: disabled ? 0.4 : 1,
      WebkitTapHighlightColor: "transparent",
      transition: "transform 0.1s",
      minHeight: 72,
    }}>
      <span style={{ fontSize: large ? 32 : 26 }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 700, textAlign: "center", lineHeight: 1.2 }}>{label}</span>
    </button>
  );
}

function InfoRow({ label, value, accent }) {
  if (!value) return null;
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
    }}>
      <span style={{ fontSize: 13, color: "#64748b" }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: accent || "#e2e8f0", textAlign: "right", maxWidth: "60%" }}>{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ padding: "0 14px", marginBottom: 14 }}>
      <div style={{
        background: "#1e293b", borderRadius: 16, padding: 16,
        border: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", letterSpacing: 0.8, marginBottom: 10, textTransform: "uppercase" }}>
          {title}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function CarrierLoadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { carrierEmail } = useAuth();
  const apiBase = useMemo(() => getApiBase(), []);
  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [updating, setUpdating] = useState(false);
  const [bidAmount, setBidAmount] = useState("");
  const [bidNotes, setBidNotes] = useState("");
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidMsg, setBidMsg] = useState("");

  const submitBid = async () => {
    if (!bidAmount) return;
    setBidSubmitting(true);
    setBidMsg("");
    try {
      const res = await fetch(`${apiBase}/carrier/loads/${id}/bid`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-carrier-email": carrierEmail || "tim@symplecode.com",
        },
        body: JSON.stringify({
          amountCents: Math.round(parseFloat(bidAmount) * 100),
          message: bidNotes || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setBidMsg("✅ Bid submitted successfully!");
        setLoad(prev => ({ ...prev, myBidCents: Math.round(parseFloat(bidAmount) * 100) }));
      } else {
        setBidMsg(`❌ ${data.detail || data.error || "Failed to submit bid"}`);
      }
    } catch (e) {
      setBidMsg(`❌ ${e.message}`);
    } finally {
      setBidSubmitting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/carrier/loads/${id}`, {
          headers: { "x-carrier-email": carrierEmail || "tim@symplecode.com" },
        });
        const data = await res.json();
        // Flatten: merge load properties to top level, keep myBid/myTender/etc
        const flat = { ...data.load, myBid: data.myBid, myTender: data.myTender, documents: data.documents, hasPod: data.hasPod };
        setLoad(flat);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [id, apiBase, carrierEmail]);

  const updateStatus = async (status) => {
    setUpdating(true); setStatusMsg("");
    try {
      const res = await fetch(`${apiBase}/carrier/loads/${id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-carrier-email": carrierEmail || "tim@symplecode.com" },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        setStatusMsg(`✅ ${status}`);
        setLoad(prev => ({ ...prev, status }));
      } else {
        const d = await res.json();
        setStatusMsg(`❌ ${d.detail || "Failed"}`);
      }
    } catch (e) { setStatusMsg(`❌ ${e.message}`); }
    finally { setUpdating(false); }
  };

  const getNextStatus = () => {
    if (!load) return null;
    const map = {
      BOOKED: "DISPATCHED", DISPATCHED: "AT_PICKUP", AT_PICKUP: "PICKED_UP",
      PICKED_UP: "IN_TRANSIT", IN_TRANSIT: "AT_DELIVERY", AT_DELIVERY: "DELIVERED",
    };
    return map[load.status] || null;
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "80px 20px", color: "#64748b" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🚛</div>Loading...
    </div>
  );

  if (error || !load) return (
    <div style={{ padding: 20 }}>
      <div style={{ textAlign: "center", padding: "40px 20px", color: "#fca5a5", background: "rgba(239,68,68,0.08)", borderRadius: 16 }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        {error || "Load not found"}
        <div style={{ marginTop: 16 }}>
          <button onClick={() => navigate(-1)} style={{
            padding: "12px 24px", borderRadius: 12, border: "1px solid #ef4444",
            background: "transparent", color: "#ef4444", fontWeight: 700, cursor: "pointer",
          }}>← Go Back</button>
        </div>
      </div>
    </div>
  );

  const rate = load.rateAmountCents ? `$${(load.rateAmountCents / 100).toLocaleString()}` : "—";
  const rpm = load.rateAmountCents && load.distanceMiles
    ? `$${(load.rateAmountCents / 100 / load.distanceMiles).toFixed(2)}/mi` : "";
  const next = getNextStatus();
  const nextStep = next ? STATUS_PIPELINE.find(s => s.key === next) : null;

  return (
    <div style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}>
      {/* ── Header ── */}
      <div style={{
        padding: "12px 14px 14px",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        {/* Back + title + rate */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate(-1)} style={{
            width: 40, height: 40, borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.05)", color: "#94a3b8", cursor: "pointer",
            fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center",
            WebkitTapHighlightColor: "transparent", flexShrink: 0,
          }}>←</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 20, fontWeight: 900, color: "#f1f5f9",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {load.referenceNumber || load.loadNumber || "Load"}
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {load.equipmentType || "Dry Van"}{load.distanceMiles ? ` · ${load.distanceMiles} mi` : ""}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#22c55e" }}>{rate}</div>
            {rpm && <div style={{ fontSize: 11, color: "#64748b" }}>{rpm}</div>}
          </div>
        </div>

        <StatusPipeline current={load.status} />
      </div>

      {/* Toast */}
      {statusMsg && (
        <div style={{
          margin: "10px 14px 0", padding: "10px 14px", borderRadius: 10,
          background: statusMsg.includes("❌") ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)",
          color: statusMsg.includes("❌") ? "#fca5a5" : "#86efac",
          fontSize: 13, fontWeight: 600,
        }}>{statusMsg}</div>
      )}

      {/* ── Quick Actions ── */}
      <div style={{ padding: "14px 14px 6px", display: "flex", gap: 8 }}>
        {next && (
          <ActionBtn
            icon={nextStep?.icon || "➡️"}
            label={`Mark ${nextStep?.label || next}`}
            color="#22c55e"
            onClick={() => updateStatus(next)}
            disabled={updating}
            large
          />
        )}
        <ActionBtn
          icon="📦" label="Capture POD" color="#ef4444"
          onClick={() => navigate(`/app/carrier/loads/${id}/pod`)}
          disabled={!["IN_TRANSIT", "AT_DELIVERY", "DELIVERED"].includes(load.status)}
          large={!next}
        />
        <ActionBtn
          icon="💵" label="Quick Pay" color="#f59e0b"
          onClick={() => navigate("/app/carrier/factoring")}
          disabled={!["DELIVERED", "CLOSED"].includes(load.status)}
        />
      </div>

      {/* ── Place Bid ── */}
      {load.status === "POSTED" && (
        <Section title="💰 Place Your Bid">
          {load.myBidCents ? (
            <div>
              <div style={{
                padding: "12px 16px", borderRadius: 10,
                background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div>
                  <div style={{ fontSize: 13, color: "#86efac", fontWeight: 600 }}>Your Current Bid</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: "#22c55e" }}>
                    ${(load.myBidCents / 100).toLocaleString()}
                  </div>
                </div>
                <span style={{
                  padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: "#064e3b", color: "#6ee7b7",
                }}>Submitted</span>
              </div>
              <div style={{ marginTop: 12, fontSize: 13, color: "#94a3b8" }}>
                Want to update your bid? Enter a new amount below.
              </div>
            </div>
          ) : null}
          <div style={{ marginTop: load.myBidCents ? 8 : 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  {load.myBidCents ? "Update Bid ($)" : "Your Bid ($)"}
                </label>
                <input
                  type="number" inputMode="decimal"
                  value={bidAmount}
                  onChange={e => setBidAmount(e.target.value)}
                  placeholder={load.rateAmountCents ? `Posted rate: $${(load.rateAmountCents / 100).toLocaleString()}` : "Enter amount"}
                  style={{
                    width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 16, fontWeight: 700,
                    border: "1px solid #334155", background: "#1e293b", color: "#f1f5f9",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <button
                onClick={submitBid}
                disabled={!bidAmount || bidSubmitting}
                style={{
                  padding: "12px 20px", borderRadius: 10, border: "none", fontWeight: 800, fontSize: 15,
                  background: bidAmount && !bidSubmitting ? "#22c55e" : "#334155",
                  color: bidAmount && !bidSubmitting ? "white" : "#64748b",
                  cursor: bidAmount && !bidSubmitting ? "pointer" : "default",
                  whiteSpace: "nowrap",
                }}
              >
                {bidSubmitting ? "..." : load.myBidCents ? "Update" : "Submit Bid"}
              </button>
            </div>
            <input
              type="text"
              value={bidNotes}
              onChange={e => setBidNotes(e.target.value)}
              placeholder="Add a note (optional)"
              style={{
                width: "100%", padding: "10px 14px", borderRadius: 10, fontSize: 13,
                border: "1px solid #334155", background: "#1e293b", color: "#f1f5f9",
                marginTop: 8, boxSizing: "border-box",
              }}
            />
          </div>
          {bidMsg && (
            <div style={{
              marginTop: 10, padding: "10px 14px", borderRadius: 10,
              background: bidMsg.includes("❌") ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)",
              color: bidMsg.includes("❌") ? "#fca5a5" : "#86efac",
              fontSize: 13, fontWeight: 600,
            }}>{bidMsg}</div>
          )}
        </Section>
      )}

      {/* ── Route ── */}
      <Section title="Route">
        {/* Pickup */}
        <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 2 }}>
            <div style={{ width: 12, height: 12, borderRadius: 12, background: "#3b82f6", border: "2px solid #0f172a" }} />
            <div style={{ width: 2, flex: 1, background: "linear-gradient(#3b82f6, #ef4444)", minHeight: 28 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#3b82f6", letterSpacing: 0.5, marginBottom: 2 }}>PICKUP</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9" }}>
              {fmtAddr(load.originAddress, load.originCity, load.originState)}
            </div>
            {load.pickupStart && (
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                {new Date(load.pickupStart).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            )}
          </div>
        </div>
        {/* Delivery */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ width: 12, height: 12, borderRadius: 12, background: "#ef4444", border: "2px solid #0f172a" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", letterSpacing: 0.5, marginBottom: 2 }}>DELIVERY</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9" }}>
              {fmtAddr(load.destAddress, load.destCity, load.destState)}
            </div>
            {load.deliveryStart && (
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                {new Date(load.deliveryStart).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* ── Details ── */}
      <Section title="Load Details">
        <InfoRow label="Load #" value={load.loadNumber} />
        <InfoRow label="Reference" value={load.referenceNumber} />
        <InfoRow label="Equipment" value={load.equipmentType} />
        <InfoRow label="Weight" value={load.weightLbs ? `${load.weightLbs.toLocaleString()} lbs` : null} />
        <InfoRow label="Distance" value={load.distanceMiles ? `${load.distanceMiles} mi` : null} />
        <InfoRow label="Rate" value={rate} accent="#22c55e" />
        <InfoRow label="Rate/Mile" value={rpm} accent="#22c55e" />
        <InfoRow label="Commodity" value={load.commodity} />
        <InfoRow label="Notes" value={load.notes || load.specialInstructions} />
      </Section>

      {/* ── Documents ── */}
      {load.documents && load.documents.length > 0 && (
        <Section title="Documents">
          {load.documents.map((doc, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 0",
              borderBottom: i < load.documents.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
            }}>
              <span style={{ fontSize: 20 }}>📄</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0" }}>{doc.name || doc.documentType || "Document"}</div>
                <div style={{ fontSize: 11, color: "#64748b" }}>{doc.status || "Available"}</div>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* ── Contacts ── */}
      <Section title="Quick Contacts">
        {load.brokerPhone && (
          <a href={`tel:${load.brokerPhone}`} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "14px", borderRadius: 12, marginBottom: 8,
            background: "rgba(59,130,246,0.08)", textDecoration: "none",
            color: "#93c5fd",
            WebkitTapHighlightColor: "transparent",
          }}>
            <span style={{ fontSize: 24 }}>📞</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Call Broker</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{load.brokerPhone}</div>
            </div>
          </a>
        )}
        {load.brokerCompany && (
          <div style={{
            padding: "10px 14px", borderRadius: 12, marginBottom: 8,
            background: "rgba(255,255,255,0.03)",
            fontSize: 13, color: "#94a3b8",
          }}>
            🏢 {load.brokerCompany}{load.brokerName ? ` — ${load.brokerName}` : ""}
          </div>
        )}
        <a href="tel:911" style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px", borderRadius: 12,
          background: "rgba(239,68,68,0.06)", textDecoration: "none",
          color: "#fca5a5",
          WebkitTapHighlightColor: "transparent",
        }}>
          <span style={{ fontSize: 24 }}>🚨</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Emergency</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>Call 911</div>
          </div>
        </a>
      </Section>
    </div>
  );
}
